import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Download, FileText, TrendingUp, Target, DollarSign, MessageSquare, Search, Star, ChevronDown, Users, Wrench, Package, Sparkles, Target as TargetIcon, Scale } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';

interface ReviewEvidence {
  excerpt: string;
  product_name: string;
  rating: number;
  sentiment: number;
  full_context?: string;
}

interface Question {
  text: string;
  search_volume: number;
  cpc: number;
  opportunity_score: number;
  difficulty_score: number;
  has_featured_snippet?: boolean;
  has_paa?: boolean;
  sources?: string[];
  source_count?: number;
  review_evidence?: ReviewEvidence[];
  status?: string;
  answer_type?: string;
  answer_type_confidence?: number;
  answer_type_reasoning?: string;
  answer_type_secondary?: string;
  answer_type_subdomain?: string;
  answer_type_label?: string;
}

interface Segment {
  type?: string;  // Old format
  segment_name?: string;  // New Enhanced format
  segment_id?: number;  // New Enhanced format
  questions: Question[];
  total?: number;
  avgVolume?: number;
  avgCpc?: number;
}

interface WheelData {
  segments: Segment[];
  totalQuestions: number;
  highOpportunityQuestions: Question[];
  metrics: {
    avgSearchVolume: number;
    avgCpc: number;
    totalSearchVolume: number;
  };
}

interface QuestionWheelProps {
  centerTerm: string;
  data: WheelData;
  wheelCategories?: string[]; // Categories from the wheel metadata
  onQuestionClick?: (question: Question) => void;
  onExport?: (format: 'csv' | 'json') => void;
}

// Category definitions matching the auto-categorization script
const CATEGORY_DEFINITIONS: Record<string, {name: string; icon: any; color: string; bgColor: string; borderColor: string}> = {
  'sector_tech': {
    name: 'Sector Technologies',
    icon: Wrench,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200'
  },
  'product_categories': {
    name: 'Product Categories',
    icon: Package,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200'
  },
  'benefits': {
    name: 'Benefits',
    icon: Sparkles,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200'
  },
  'use_cases': {
    name: 'Use Cases',
    icon: TargetIcon,
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200'
  },
  'comparison_topics': {
    name: 'Comparisons',
    icon: Scale,
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-50',
    borderColor: 'border-indigo-200'
  }
};

export const QuestionWheel: React.FC<QuestionWheelProps> = ({
  centerTerm,
  data,
  wheelCategories = [],
  onQuestionClick,
  onExport
}) => {
  const [selectedType, setSelectedType] = useState<string>('all');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const getOpportunityColor = (score: number) => {
    if (score >= 70) return 'bg-green-500';
    if (score >= 40) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getOpportunityLabel = (score: number) => {
    if (score >= 70) return 'High';
    if (score >= 40) return 'Medium';
    return 'Low';
  };

  // Calculate all statistics from filtered data (parent applies filters)
  const stats = React.useMemo(() => {
    const allQuestions = data.segments.flatMap(s => s.questions);

    // Source stats
    const reviewCount = allQuestions.filter(q => q.sources?.includes('reviews')).length;
    const paaCount = allQuestions.filter(q => q.sources?.includes('google_paa')).length;
    const multiSourceCount = allQuestions.filter(q => (q.source_count || 0) >= 2).length;
    const reviewValidated = allQuestions.filter(q =>
      q.sources?.includes('reviews') && q.sources?.includes('google_paa')
    ).length;

    // Recalculate metrics from filtered questions
    const totalQuestions = allQuestions.length;
    const highOpportunityCount = allQuestions.filter(q => q.opportunity_score >= 70).length;
    const totalSearchVolume = allQuestions.reduce((sum, q) => sum + (q.search_volume || 0), 0);
    const totalCpc = allQuestions.reduce((sum, q) => sum + (q.cpc || 0), 0);
    const avgSearchVolume = totalQuestions > 0 ? Math.round(totalSearchVolume / totalQuestions) : 0;
    const avgCpc = totalQuestions > 0 ? (totalCpc / totalQuestions).toFixed(2) : '0.00';

    return {
      sourceStats: { reviewCount, paaCount, multiSourceCount, reviewValidated },
      totalQuestions,
      highOpportunityCount,
      avgSearchVolume,
      avgCpc
    };
  }, [data.segments]);

  // Render source badges for a question
  const renderSourceBadges = (question: Question) => {
    if (!question.sources || question.sources.length === 0) return null;

    return (
      <div className="flex gap-1 mt-1 flex-wrap">
        {question.sources.includes('reviews') && (
          <Badge variant="default" className="text-xs bg-green-600 hover:bg-green-700">
            <MessageSquare className="h-3 w-3 mr-1" />
            Customer Verified
          </Badge>
        )}
        {question.sources.includes('google_paa') && (
          <Badge variant="default" className="text-xs bg-blue-600 hover:bg-blue-700">
            <Search className="h-3 w-3 mr-1" />
            Google PAA
          </Badge>
        )}
        {(question.source_count || 0) >= 3 && (
          <Badge variant="default" className="text-xs bg-purple-600 hover:bg-purple-700">
            <Star className="h-3 w-3 mr-1" />
            Multi-Source
          </Badge>
        )}
      </div>
    );
  };

  // Render category badges for a question (inherited from wheel)
  const renderCategoryBadges = () => {
    if (!wheelCategories || wheelCategories.length === 0) return null;

    return (
      <div className="flex gap-1 mt-1 flex-wrap">
        {wheelCategories.map(category => {
          const def = CATEGORY_DEFINITIONS[category];
          if (!def) return null;
          const Icon = def.icon;
          return (
            <Badge
              key={category}
              variant="outline"
              className={`${def.bgColor} ${def.borderColor} text-xs`}
            >
              <Icon className={`h-3 w-3 mr-1 ${def.color}`} />
              {def.name}
            </Badge>
          );
        })}
      </div>
    );
  };

  // Apply question type filter only (source and answer type filtering done by parent)
  const filteredSegments = selectedType === 'all'
    ? data.segments
    : data.segments.filter(s => {
        const segmentKey = s.type || s.segment_name || '';
        return segmentKey === selectedType;
      });

  return (
    <div className="space-y-6">
      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Questions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalQuestions}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              High Opportunity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {stats.highOpportunityCount}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Score ≥ 70
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Avg Search Volume
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.avgSearchVolume.toLocaleString()}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Avg CPC
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${stats.avgCpc}</div>
          </CardContent>
        </Card>
      </div>

      {/* Source Statistics Card - NEW! */}
      {(stats.sourceStats.reviewCount > 0 || stats.sourceStats.paaCount > 0 || stats.sourceStats.multiSourceCount > 0) && (
        <Card className="border-2 border-primary/20 bg-primary/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Star className="h-5 w-5 text-primary" />
              Question Sources & Validation
            </CardTitle>
            <CardDescription>
              Questions validated by multiple data sources for higher confidence
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {stats.sourceStats.reviewCount > 0 && (
                <div className="text-center p-3 bg-green-50 dark:bg-green-950 rounded-lg border border-green-200 dark:border-green-800">
                  <div className="text-2xl font-bold text-green-700 dark:text-green-300">
                    {stats.sourceStats.reviewCount}
                  </div>
                  <div className="text-xs text-green-600 dark:text-green-400 mt-1 flex items-center justify-center gap-1">
                    <MessageSquare className="h-3 w-3" />
                    Customer Reviews
                  </div>
                </div>
              )}
              {stats.sourceStats.paaCount > 0 && (
                <div className="text-center p-3 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="text-2xl font-bold text-blue-700 dark:text-blue-300">
                    {stats.sourceStats.paaCount}
                  </div>
                  <div className="text-xs text-blue-600 dark:text-blue-400 mt-1 flex items-center justify-center gap-1">
                    <Search className="h-3 w-3" />
                    Google PAA
                  </div>
                </div>
              )}
              {stats.sourceStats.multiSourceCount > 0 && (
                <div className="text-center p-3 bg-purple-50 dark:bg-purple-950 rounded-lg border border-purple-200 dark:border-purple-800">
                  <div className="text-2xl font-bold text-purple-700 dark:text-purple-300">
                    {stats.sourceStats.multiSourceCount}
                  </div>
                  <div className="text-xs text-purple-600 dark:text-purple-400 mt-1 flex items-center justify-center gap-1">
                    <Star className="h-3 w-3" />
                    Multi-Source
                  </div>
                </div>
              )}
              {stats.sourceStats.reviewValidated > 0 && (
                <div className="text-center p-3 bg-amber-50 dark:bg-amber-950 rounded-lg border border-amber-200 dark:border-amber-800">
                  <div className="text-2xl font-bold text-amber-700 dark:text-amber-300">
                    {stats.sourceStats.reviewValidated}
                  </div>
                  <div className="text-xs text-amber-600 dark:text-amber-400 mt-1 flex items-center justify-center gap-1">
                    <Users className="h-3 w-3" />
                    Customer + Search
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Center Term Display */}
      <Card className="bg-gradient-to-br from-primary/10 to-primary/5">
        <CardHeader>
          <CardTitle className="text-2xl text-center">{centerTerm}</CardTitle>
          <CardDescription className="text-center">
            Question opportunity analysis powered by AI and SERP data
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Category Filter */}
      {wheelCategories && wheelCategories.length > 0 && (
        <Card className="border-2 border-primary/20">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Scale className="h-4 w-4" />
              Question Categories
            </CardTitle>
            <CardDescription className="text-xs">
              All questions in this wheel belong to these categories
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {wheelCategories.map(category => {
                const def = CATEGORY_DEFINITIONS[category];
                if (!def) return null;
                const Icon = def.icon;
                return (
                  <Badge
                    key={category}
                    variant="outline"
                    className={`${def.bgColor} ${def.borderColor}`}
                  >
                    <Icon className={`h-4 w-4 mr-2 ${def.color}`} />
                    {def.name}
                  </Badge>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Question Type Filter */}
      <Tabs value={selectedType} onValueChange={setSelectedType} className="w-full">
        <div className="flex justify-between items-center mb-4 flex-wrap gap-4">
          <TabsList>
            <TabsTrigger value="all">All Questions</TabsTrigger>
            {data.segments.map((segment, idx) => {
              const segmentKey = segment.type || segment.segment_name || `segment-${idx}`;
              const segmentLabel = (segment.type || segment.segment_name || `Segment ${idx + 1}`).toUpperCase();
              const questionCount = segment.total || segment.questions?.length || 0;
              return (
                <TabsTrigger key={segmentKey} value={segmentKey}>
                  {segmentLabel} ({questionCount})
                </TabsTrigger>
              );
            })}
          </TabsList>

          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => onExport?.('csv')}>
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            <Button variant="outline" size="sm" onClick={() => onExport?.('json')}>
              <FileText className="h-4 w-4 mr-2" />
              Export JSON
            </Button>
          </div>
        </div>

        <TabsContent value={selectedType} className="space-y-4">
          {filteredSegments.map((segment, idx) => {
            const segmentKey = segment.type || segment.segment_name || `segment-${idx}`;
            const segmentLabel = segment.type || segment.segment_name || `Segment ${idx + 1}`;
            const questionCount = segment.total || segment.questions?.length || 0;
            const avgVolume = segment.avgVolume || 0;
            const avgCpc = segment.avgCpc || 0;

            return (
              <Card key={segmentKey}>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle className="text-lg uppercase">{segmentLabel} Questions</CardTitle>
                      <CardDescription>
                        {questionCount} questions • Avg volume: {avgVolume.toLocaleString()} • Avg CPC: ${avgCpc.toFixed(2)}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {segment.questions.slice(0, 10).map((question, idx) => {
                    const hasReviewEvidence = question.review_evidence && question.review_evidence.length > 0;

                    return (
                      <div key={idx} className="border rounded-lg">
                        {/* Main Question Display */}
                        <div
                          className="flex items-center justify-between p-3 hover:bg-accent cursor-pointer transition-colors"
                          onClick={() => !hasReviewEvidence && onQuestionClick?.(question)}
                        >
                          <div className="flex-1">
                            <p className="font-medium">{question.text}</p>
                            <div className="flex gap-4 mt-1 text-sm text-muted-foreground flex-wrap items-center">
                              <span className="flex items-center gap-1">
                                <TrendingUp className="h-3 w-3" />
                                {(question.search_volume || 0).toLocaleString()} searches
                              </span>
                              <span className="flex items-center gap-1">
                                <DollarSign className="h-3 w-3" />
                                ${(question.cpc || 0).toFixed(2)} CPC
                              </span>
                              {question.has_featured_snippet && (
                                <Badge variant="secondary">Featured Snippet</Badge>
                              )}
                            </div>
                            {/* Source Badges */}
                            {renderSourceBadges(question)}
                            {/* Category Badges */}
                            {renderCategoryBadges()}
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <div className="flex items-center gap-2">
                                <Target className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm font-medium">
                                  Opportunity: {question.opportunity_score}
                                </span>
                              </div>
                              <div className="text-xs text-muted-foreground">
                                Difficulty: {question.difficulty_score}
                              </div>
                            </div>
                            <div
                              className={`w-3 h-3 rounded-full ${getOpportunityColor(question.opportunity_score)}`}
                              title={`${getOpportunityLabel(question.opportunity_score)} Opportunity`}
                            />
                          </div>
                        </div>

                        {/* Review Evidence Panel */}
                        {hasReviewEvidence && (
                          <Collapsible>
                            <CollapsibleTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="w-full justify-start text-xs border-t rounded-none hover:bg-muted"
                              >
                                <Users className="h-3 w-3 mr-2" />
                                View Customer Feedback ({question.review_evidence!.length} review{question.review_evidence!.length > 1 ? 's' : ''})
                                <ChevronDown className="h-3 w-3 ml-auto" />
                              </Button>
                            </CollapsibleTrigger>
                            <CollapsibleContent className="border-t bg-muted/30">
                              <div className="p-3 space-y-2">
                                {question.review_evidence!.slice(0, 3).map((evidence, evidenceIdx) => (
                                  <div
                                    key={evidenceIdx}
                                    className="text-xs bg-background p-3 rounded border"
                                  >
                                    <p className="italic text-muted-foreground mb-2">
                                      "{evidence.excerpt}"
                                    </p>
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <Badge variant="outline" className="text-xs">
                                        {evidence.product_name}
                                      </Badge>
                                      <Badge
                                        variant={evidence.sentiment > 0 ? "default" : "destructive"}
                                        className="text-xs"
                                      >
                                        {evidence.sentiment > 0 ? '👍' : '👎'}
                                        {' '}
                                        {Math.abs(evidence.sentiment * 100).toFixed(0)}% sentiment
                                      </Badge>
                                      <Badge variant="secondary" className="text-xs">
                                        ⭐ {evidence.rating}/5
                                      </Badge>
                                    </div>
                                  </div>
                                ))}
                                {question.review_evidence!.length > 3 && (
                                  <p className="text-xs text-muted-foreground text-center">
                                    + {question.review_evidence!.length - 3} more review{question.review_evidence!.length - 3 > 1 ? 's' : ''}
                                  </p>
                                )}
                              </div>
                            </CollapsibleContent>
                          </Collapsible>
                        )}
                      </div>
                    );
                  })}
                  {segment.questions.length > 10 && (
                    <p className="text-sm text-muted-foreground text-center pt-2">
                      + {segment.questions.length - 10} more questions
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
            );
          })}
        </TabsContent>
      </Tabs>

      {/* Legend */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Opportunity Score Legend</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-6">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500" />
              <span className="text-sm">High (70-100) - Quick wins</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-yellow-500" />
              <span className="text-sm">Medium (40-70) - Moderate effort</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              <span className="text-sm">Low (0-40) - High competition</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
